import type { ScheduleEvent, ScheduleEventType } from '../../scheduler/types';
import { formatScheduleTimeForDisplay } from '../../scheduler/time-format';
import type { CommunicationAudienceSelection } from './types';

export const LINKABLE_REHEARSAL_EVENT_TYPES: ScheduleEventType[] = [
  'scene',
  'act',
  'run',
  'rehearsal-scene',
  'tech',
  'performance',
];

export function isLinkableCallSheetEvent(event: ScheduleEvent): boolean {
  return LINKABLE_REHEARSAL_EVENT_TYPES.includes(event.type) || Boolean(event.attendees?.length);
}

export function firstLinkableCallSheetEventIdForDate(
  events: ScheduleEvent[],
  callDate: string
): string {
  const linkableEvents = events
    .filter((event) => event.date === callDate && isLinkableCallSheetEvent(event))
    .toSorted((a, b) => a.startTime.localeCompare(b.startTime));

  return linkableEvents[0]?.id ?? '';
}

export function callSheetSelectionForLinkedEvent(
  configuredDefault: CommunicationAudienceSelection,
  hasLinkedEvent: boolean
): CommunicationAudienceSelection {
  const standingAudienceIds = [...configuredDefault.standingAudienceIds];
  const contextualAudienceKeys = hasLinkedEvent
    ? [...configuredDefault.contextualAudienceKeys]
    : configuredDefault.contextualAudienceKeys.filter((key) => key !== 'called-recipients');

  return { standingAudienceIds, contextualAudienceKeys };
}

function eventFallbackLabel(type: ScheduleEventType): string {
  switch (type) {
    case 'scene':
      return 'Scene Call';
    case 'act':
      return 'Act Call';
    case 'run':
      return 'Full Run';
    case 'custom':
      return 'Custom Event';
    case 'rehearsal-scene':
      return 'Rehearsal Segment';
    case 'meeting':
      return 'Meeting';
    case 'tech':
      return 'Tech Rehearsal';
    case 'performance':
      return 'Performance';
    case 'deadline':
      return 'Deadline';
  }
}

export function callSheetEventLabel(event: ScheduleEvent): string {
  const titleOrFallback = event.title.trim() || eventFallbackLabel(event.type);

  return `${event.date} ${formatScheduleTimeForDisplay(event.startTime)} ${titleOrFallback}`;
}
